import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Camera, Flame, Crown, Star, Heart, Trophy, TrendingUp, Award,
  Wand2, ChevronRight, Gift, Zap, LayoutDashboard, CheckCircle2, Target
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fileUrl } from "@/api";

const BADGE_ICONS = { sparkles: Sparkles, star: Star, heart: Heart, flame: Flame, crown: Crown };
const MILESTONE_ICONS = { sparkles: Sparkles, camera: Camera, flame: Flame, crown: Crown, trophy: Trophy };

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function PersonalizedHome({ onNewAnalysis }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true); setError(false);
    try {
      const res = await api.get("/home");
      setData(res.data);
    } catch (e) {
      setError(true);
      toast.error("Couldn't load your home. Tap retry.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Sparkles className="w-10 h-10 text-primary animate-spin" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="home-error">
        <p className="text-muted-foreground">We couldn't load your home right now.</p>
        <Button onClick={load} className="rounded-full bg-primary hover:bg-primary/90" data-testid="home-retry-btn">Retry</Button>
      </div>
    );
  }

  const g = data.gamification;

  return (
    <div className="min-h-screen px-4 py-8" data-testid="personalized-home">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Greeting + New analysis */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{greeting()},</p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground">{data.name} 💅</h1>
          </div>
          <Button onClick={onNewAnalysis} className="rounded-full bg-primary hover:bg-primary/90 shadow-soft" data-testid="home-new-analysis-btn">
            <Camera className="w-4 h-4 mr-1" /> New analysis
          </Button>
        </motion.div>

        {/* XP / Level card */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-heavy rounded-3xl p-6" data-testid="xp-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-playfair font-bold text-lg" data-testid="level-badge">
                {g.level}
              </div>
              <div>
                <p className="font-semibold text-foreground">{g.level_title}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Zap className="w-3 h-3 text-amber-500" />{g.xp} XP</p>
              </div>
            </div>
            {g.next_level_title && (
              <p className="text-xs text-muted-foreground text-right">{g.xp_for_next} XP to<br /><span className="font-medium text-foreground">{g.next_level_title}</span></p>
            )}
          </div>
          <div className="h-3 rounded-full bg-primary/10 overflow-hidden">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500" initial={{ width: 0 }} animate={{ width: `${g.progress_pct}%` }} transition={{ duration: 1 }} data-testid="xp-progress" />
          </div>
        </motion.div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="glass rounded-2xl p-4 text-center" data-testid="home-streak">
            <Flame className="w-5 h-5 mx-auto mb-1 text-rose-500" />
            <p className="font-playfair text-2xl font-bold text-foreground">{data.current_streak}w</p>
            <p className="text-xs text-muted-foreground">Streak</p>
          </div>
          <div className="glass rounded-2xl p-4 text-center">
            <Trophy className="w-5 h-5 mx-auto mb-1 text-amber-500" />
            <p className="font-playfair text-2xl font-bold text-foreground">{data.best_score}</p>
            <p className="text-xs text-muted-foreground">Best score</p>
          </div>
          <div className="glass rounded-2xl p-4 text-center">
            <Sparkles className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="font-playfair text-2xl font-bold text-foreground">{data.total_analyses}</p>
            <p className="text-xs text-muted-foreground">Analyses</p>
          </div>
        </div>

        {/* Streak reward nudge */}
        {data.next_streak_reward && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid="streak-reward">
            <Gift className="w-6 h-6 text-primary shrink-0" />
            <p className="text-sm text-foreground/80">
              {data.current_streak >= 1
                ? <>Keep it up! Reach a <b>{data.next_streak_reward.weeks}-week</b> streak to unlock <b>{data.next_streak_reward.reward}</b></>
                : <>Upload weekly to build a streak and unlock <b>{data.next_streak_reward.reward}</b></>}
            </p>
          </motion.div>
        )}

        {/* Today's trend */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h3 className="font-playfair text-xl font-bold text-foreground mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Today's trend</h3>
          <button onClick={() => navigate("/occasions")} className="w-full relative rounded-3xl overflow-hidden h-44 group" data-testid="today-trend">
            <img src={data.today_trend.image} alt={data.today_trend.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4 text-left">
              <span className="text-2xl">{data.today_trend.icon}</span>
              <p className="text-white font-playfair text-2xl font-bold">{data.today_trend.label} nails</p>
              <p className="text-white/80 text-sm flex items-center gap-1">Get the AI look <ChevronRight className="w-4 h-4" /></p>
            </div>
          </button>
        </motion.div>

        {/* Recommended styles */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-playfair text-xl font-bold text-foreground flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" />Recommended for you</h3>
            <button onClick={() => navigate("/occasions")} className="text-sm text-primary font-medium flex items-center gap-1" data-testid="see-all-occasions">See all <ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {data.recommended.map((occ) => (
              <button key={occ.id} onClick={() => navigate("/occasions")} className="relative rounded-2xl overflow-hidden aspect-[3/4] group" data-testid={`rec-${occ.id}`}>
                <img src={occ.image} alt={occ.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <p className="absolute bottom-2 left-2 text-white text-sm font-semibold">{occ.icon} {occ.label}</p>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Recent scores */}
        {data.recent.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-playfair text-xl font-bold text-foreground flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Recent scores</h3>
              <button onClick={() => navigate("/dashboard")} className="text-sm text-primary font-medium flex items-center gap-1" data-testid="see-dashboard">Dashboard <ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-3">
              {data.recent.map((r) => (
                <div key={r.id} className="glass rounded-2xl p-3 flex-1 text-center" data-testid="recent-score">
                  {r.image_path ? <img src={fileUrl(r.image_path)} alt="" className="w-full h-20 object-cover rounded-xl mb-2" /> : <div className="w-full h-20 rounded-xl bg-secondary/40 mb-2 flex items-center justify-center"><Sparkles className="w-5 h-5 text-primary/40" /></div>}
                  <p className="font-playfair text-xl font-bold text-primary">{r.score}/20</p>
                  <p className="text-[11px] text-muted-foreground">{format(new Date(r.date), "MMM d")}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Milestones */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h3 className="font-playfair text-xl font-bold text-foreground mb-3 flex items-center gap-2"><Target className="w-5 h-5 text-primary" />Milestones</h3>
          <div className="space-y-2">
            {data.milestones.map((m) => {
              const Icon = MILESTONE_ICONS[m.icon] || Target;
              const pct = Math.min(100, Math.round((m.current / m.target) * 100));
              return (
                <div key={m.id} className={`glass rounded-2xl p-3 flex items-center gap-3 ${m.done ? "opacity-100" : ""}`} data-testid={`milestone-${m.id}`}>
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${m.done ? "bg-green-100 text-green-600" : "bg-secondary/50 text-primary"}`}>
                    {m.done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </span>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${m.done ? "text-foreground" : "text-foreground/80"}`}>{m.title}</p>
                    {!m.done && (
                      <div className="h-1.5 rounded-full bg-primary/10 mt-1 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
                    )}
                  </div>
                  {!m.done && <span className="text-xs text-muted-foreground shrink-0">{m.current}/{m.target}</span>}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Badges */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h3 className="font-playfair text-xl font-bold text-foreground mb-3 flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" />Badges</h3>
          <div className="grid grid-cols-5 gap-3">
            {data.badges.map((b) => {
              const Icon = BADGE_ICONS[b.icon] || Award;
              return (
                <div key={b.id} className={`text-center rounded-2xl p-3 ${b.earned ? "bg-gradient-to-br from-pink-100 to-purple-100" : "bg-secondary/20 opacity-50"}`} title={b.desc} data-testid={`home-badge-${b.id}`}>
                  <Icon className={`w-6 h-6 mx-auto mb-1 ${b.earned ? "text-primary" : "text-muted-foreground"}`} />
                  <p className="text-[10px] font-medium text-foreground leading-tight">{b.title}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        <div className="flex gap-3 pt-2">
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="flex-1 rounded-full border-primary text-primary hover:bg-primary/5" data-testid="home-dashboard-btn"><LayoutDashboard className="w-4 h-4 mr-1" />Progress</Button>
          <Button onClick={() => navigate("/occasions")} variant="outline" className="flex-1 rounded-full border-primary text-primary hover:bg-primary/5" data-testid="home-inspire-btn"><Wand2 className="w-4 h-4 mr-1" />Inspire</Button>
        </div>
      </div>
    </div>
  );
}
